import 'src/global.css'

import { ErrorBoundary } from '@appsignal/react'
import { Suspense, useEffect, useState } from 'react'
import { Provider } from 'react-redux'
// import { BrowserRouter } from 'react-router-dom'
import ReactPixel from 'react-facebook-pixel'
import ReactGA from 'react-ga4'
import { HelmetProvider } from 'react-helmet-async'
import { hotjar } from 'react-hotjar'
import { I18nextProvider } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import brandConfig from 'brandConfig'
import { MotionLazy } from 'src/components/shared/animate/motion-lazy'
import { ProgressBar } from 'src/components/shared/progress-bar'
import { Snackbar } from 'src/components/shared/snackbar'
import AppRoutes from './AppRoutes'
import { SplashScreen } from './components/shared/loading-screen'
import { APP_CONFIG, getAppSignalConfig } from './constants/appConfig'
import { useScrollTop } from './hooks/useScrollTop'
import i18n from './i18n'
import { getIndexStore } from './store'
import { themeConfig, ThemeProvider } from './theme'
import { isRunningTesting } from './util/app/app.helpers'
import { clearCacheOnNewAppVersion } from './util/cache.helpers'
import { logAppVersion } from './util/log.helpers'
import { initGTM, pushDataLayer } from './util/gaTracking.helpers'
// import { useServiceWorker } from './hooks/useServiceWorker'
// import AppRefresh from './components/popups/common/AppRefresh'

logAppVersion()
const getApp = async () => {
  const store = await getIndexStore()
  const App = () => {
    useScrollTop()
    const [authId, setAuthId] = useState('')

    useEffect(() => {
      const storeUnsubscribe = store.subscribe(() => {
        const { authId } = store.getState().accountReducer.accountProfile
        if (authId) {
          setAuthId(authId)
          pushDataLayer({ authId }, 'userDataLayer')
        }
      })

      return () => {
        storeUnsubscribe()
      }
    }, [])

    const location = useLocation()
    useEffect(() => {
      clearCacheOnNewAppVersion()

      if (!isRunningTesting()) {
        // Facebook Pixel
        const facebookPixelId = APP_CONFIG.FACEBOOK_PIXEL_ID
        if (facebookPixelId) {
          ReactPixel.init(facebookPixelId, null, {
            autoConfig: true,
            debug: false
          })
          ReactPixel.pageView()
          ReactPixel.track('PageView')
        }

        // Google Tag Manager
        initGTM()

        // HotJar
        const hotjarConfig = APP_CONFIG.HOTJAR_CONFIG
        if (brandConfig.hotjarId && hotjarConfig.version) {
          hotjar.initialize(brandConfig.hotjarId, hotjarConfig.version)
        }
      }
    }, [])

    useEffect(() => {
      if (!isRunningTesting() && location) {
        // Google Analytics
        const gaTrackingId = APP_CONFIG.GA_TRACKING_ID
        if (gaTrackingId) {
          ReactGA.initialize(gaTrackingId)
          ReactGA.send({ hitType: 'pageview', page: location.pathname })
        }
        pushDataLayer(
          { event: 'pageview', pagePath: location.pathname },
          'pageViewDataLayer'
        )
      }
    }, [location])

    // @TODO: remove this before production
    // const { waitingWorker, showReload, reloadPage } = useServiceWorker()

    return (
      <ErrorBoundary {...getAppSignalConfig(authId)}>
        <HelmetProvider>
          <Provider store={store}>
            <I18nextProvider i18n={i18n}>
              <ThemeProvider
                noSsr
                defaultMode={themeConfig.defaultMode}
                modeStorageKey={themeConfig.modeStorageKey}
              >
                <MotionLazy>
                  <Snackbar />
                  <ProgressBar />
                  <Suspense fallback={<SplashScreen />}>
                    <AppRoutes />
                  </Suspense>
                  {/* {showReload && waitingWorker && (
                      <AppRefresh onSubmit={() => reloadPage()} />
                    )} */}
                </MotionLazy>
              </ThemeProvider>
            </I18nextProvider>
          </Provider>
        </HelmetProvider>
      </ErrorBoundary>
    )
  }
  return App
}

export { getApp }
